import { z } from 'zod';

const optionalText = (max, label) => z
  .string()
  .max(max, `${label}最多 ${max} 字`)
  .optional()
  .or(z.literal(''));

const optionalCountryCode = z
  .string()
  .optional()
  .or(z.literal(''))
  .refine(
    (value) => !value || /^[A-Za-z]{2}$/.test(String(value).trim()),
    '國碼須為兩碼英文字',
  );

function optionalSnCode(length, label) {
  return z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (value) => !value || new RegExp(`^[A-Za-z]{${length}}$`).test(String(value).trim()),
      `${label}須為 ${length} 碼英文字`,
    );
}

const addressRowSchema = z.object({
  id: z.string().optional(),
  addressType: z.enum(['BILLING', 'SHIPPING', 'BOTH'], {
    message: '請選擇地址類型',
  }),
  postalCode: optionalText(20, '郵遞區號'),
  address: z.string().trim().min(1, '地址不能為空').max(500, '地址最多 500 字'),
  isDefault: z.boolean().optional(),
  remark: optionalText(500, '備註'),
});

const contactRowSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1, '聯絡人姓名不能為空').max(100, '姓名最多 100 字'),
  department: optionalText(100, '部門'),
  title: optionalText(100, '職稱'),
  phone: optionalText(50, '電話'),
  mobile: optionalText(50, '手機'),
  email: optionalText(200, 'Email'),
  isDefault: z.boolean().optional(),
  remark: optionalText(500, '備註'),
});

export const customerFormSchema = z.object({
  code: z.string().trim().min(1, '代號不能為空').max(50, '代號最多 50 字'),
  name: z.string().trim().min(1, '名稱不能為空').max(100, '名稱最多 100 字'),
  chineseName: optionalText(100, '中文名'),
  englishName: optionalText(100, '英文名'),
  taxIdNumber: optionalText(50, '統編'),
  invoiceTitle: optionalText(200, '發票抬頭'),
  snCode2: optionalSnCode(2, '兩碼 SN 前綴'),
  snCode3: optionalSnCode(3, '三碼 SN 前綴'),
  taxCountryCode: optionalCountryCode,
  homepage: optionalText(200, '網址'),
  phone: optionalText(50, '電話'),
  fax: optionalText(50, '傳真'),
  remark: optionalText(500, '備註'),
  addresses: z.array(addressRowSchema),
  contacts: z.array(contactRowSchema),
}).superRefine((data, ctx) => {
  let billingDefaultCount = 0;
  let shippingDefaultCount = 0;

  (data.addresses ?? []).forEach((row) => {
    if (!row?.isDefault) {
      return;
    }
    if (row.addressType === 'BILLING' || row.addressType === 'BOTH') {
      billingDefaultCount += 1;
    }
    if (row.addressType === 'SHIPPING' || row.addressType === 'BOTH') {
      shippingDefaultCount += 1;
    }
  });

  if (billingDefaultCount > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '帳單用途最多一個預設地址',
      path: ['addresses'],
    });
  }

  if (shippingDefaultCount > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '出貨用途最多一個預設地址',
      path: ['addresses'],
    });
  }
});

export const CUSTOMER_FORM_DEFAULT_VALUES = {
  code: '',
  name: '',
  chineseName: '',
  englishName: '',
  taxIdNumber: '',
  invoiceTitle: '',
  snCode2: '',
  snCode3: '',
  taxCountryCode: '',
  homepage: '',
  phone: '',
  fax: '',
  remark: '',
  addresses: [],
  contacts: [],
};
